import { type MouseEvent, type ReactNode, useState } from 'react';

import { type TileNodeProps } from '@/components/diagrams/FlowChart/nodes/TileNode/TileNode';
import { DiagramNodeKind } from '@/types/schema';

import {
  DiagramTextField,
  type DiagramTextFieldName,
  FIELD_ATTRIBUTE,
  TEXT_FIELDS,
} from '../components/DiagramEditor/DiagramTextField';
import { useDiagramEditing } from '../contexts/diagramEditing.context';
import { type DiagramBuilderNode } from '../utils/diagram';
import { type ReactFlowDiagramNodeData } from '../utils/reactFlowDiagram';

/**
 * Returns the edit that saves `text` in `field`. A tile must have a title,
 * so an empty tile title makes no edit. Standalone text can clear its title.
 * An empty subtitle or footer is null.
 */
function getTextEdit(
  field: DiagramTextFieldName,
  text: string,
  isText: boolean
): Partial<DiagramBuilderNode> | null {
  switch (field) {
    case 'label':
      return text || isText ? { label: text } : null;
    case 'detail':
      return { detail: text };
    case 'subtitle':
    case 'footerLabel':
      return { [field]: text || null };
  }
}

/**
 * Edits the text fields of a builder node: the title, the subtitle, the
 * notes, and the footer. Every kind of builder node uses this hook, so all
 * nodes show and edit their text in the same way. The node only places the
 * fields. `fields` are the fields that the node shows. A shape shows only
 * its title.
 *
 * `renderField` returns a `DiagramTextField`, or null when the field is
 * hidden or has no text. The node gives `onDoubleClick` to the element that
 * contains its fields. A double-click edits the field under the pointer, or
 * the main field. Tab saves the field and edits the next shown field.
 *
 * A node setting can hide the notes or the footer. Tab skips a hidden field.
 * The shown footer is the `footerLabel` of the node data, such as the item
 * count of a collection, but the editor edits the saved footer.
 *
 * A field without text takes no space until the user edits the node text.
 * Then each shown field without text shows a placeholder that the user can
 * click, or reach with Tab.
 *
 * The main field of a node always shows, with a placeholder when it has no
 * text, and a double-click outside the other fields edits it. It is the
 * title of a tile or a shape, and the notes of standalone text. A setting
 * cannot hide the notes of standalone text, because they are its content.
 */
export function useDiagramTextFields(
  { id, data, isConnectable }: TileNodeProps,
  fields: readonly DiagramTextFieldName[] = TEXT_FIELDS
) {
  const nodeData = data as ReactFlowDiagramNodeData;
  const { item, footerLabel } = nodeData;
  const { editNode } = useDiagramEditing();
  const [editing, setEditing] = useState<DiagramTextFieldName | null>(null);
  const savedText: Record<DiagramTextFieldName, string> = {
    label: item.label,
    subtitle: item.subtitle ?? '',
    detail: item.detail ?? '',
    footerLabel: item.footerLabel ?? '',
  };
  const shownText = { ...savedText, footerLabel: footerLabel ?? '' };
  const isText = item.kind === DiagramNodeKind.Text;
  const mainField: DiagramTextFieldName = isText ? 'detail' : 'label';

  const visibleFields = fields.filter(
    (field) =>
      field === mainField ||
      (!(field === 'subtitle' && !item.subtitle && !editing) &&
        !(field === 'detail' && item.nodeStyle?.hideDetail) &&
        !(field === 'footerLabel' && item.nodeStyle?.hideFooterLabel))
  );

  const save = (field: DiagramTextFieldName, text: string) => {
    const edit =
      text === savedText[field] ? null : getTextEdit(field, text, isText);
    if (edit) editNode(id, edit);
  };

  const getEditor = (field: DiagramTextFieldName) => {
    const next = visibleFields[visibleFields.indexOf(field) + 1];
    return {
      onCommit: (text: string) => {
        setEditing(null);
        save(field, text);
      },
      onCancel: () => setEditing(null),
      onTab: next ? () => setEditing(next) : undefined,
      onSave: (text: string) => save(field, text),
    };
  };

  const renderField = (field: DiagramTextFieldName): ReactNode => {
    if (!visibleFields.includes(field)) return null;
    if (!shownText[field] && !editing && field !== mainField) return null;
    return (
      <DiagramTextField
        field={field}
        data={nodeData}
        text={shownText[field]}
        savedText={savedText[field]}
        editor={field === editing ? getEditor(field) : null}
        // The main field shows its placeholder before the user edits the
        // node. Then a press on it selects the node, and a double-click
        // edits it.
        onEdit={editing ? () => setEditing(field) : undefined}
      />
    );
  };

  const onDoubleClick = (event: MouseEvent<HTMLElement>) => {
    if (!isConnectable || !(event.target instanceof Element)) return;
    const field = event.target
      .closest(`[${FIELD_ATTRIBUTE}]`)
      ?.getAttribute(FIELD_ATTRIBUTE) as DiagramTextFieldName | undefined;
    setEditing(field ?? mainField);
  };

  return { renderField, onDoubleClick };
}
